// General dependencies
import {
  loadStripe,
  Stripe,
  StripeCardElement,
  SourceResult,
  PaymentRequest,
  PaymentRequestSourceEvent,
} from '@stripe/stripe-js';
import { STRIPE_INPUT_STYLING } from '@centerhealth/fe/stripe';
import { ICheckoutData, IPaymentRequestType } from '@centerhealth/common/subscribe';
import { SentryService } from '@centerhealth/fe/sentry';
import { SHIPPING_OPTIONS } from '../constants/subscribe.shipping.options.constant';
import { SubscribeSourceService } from './subscribe.source.service';

export class SubscribePaymentService {
  paymentRequest: PaymentRequest;

  card: StripeCardElement;

  cardElement: HTMLDivElement;

  stripe: Stripe;

  initialised = false;

  /**
   * Initializes Stripe for FE
   */
  public async initStripe(publishableKey: string): Promise<void> {
    if (this.initialised) return;
    try {
      this.stripe = await loadStripe(publishableKey);
      this.initialised = true;
    } catch (error) {
      SentryService.captureException(error);
    }
  }

  public async loadStripeComponents(
    total: number,
    strips: string | number,
    payRequestHandler: (event: PaymentRequestSourceEvent) => Promise<void>,
    eventHandler: (event: { elementType: 'card' }) => void,
    vendorHandler: (socialPaySupported: boolean, vendor: string) => void,
    setPayRequest: (paymentRequest: PaymentRequest) => void,
    cardElement: HTMLDivElement,
    publishableKey: string
  ): Promise<void> {
    // Initializing Stripe unless it's already initialized
    await this.initStripe(publishableKey);
    if (this === undefined || this.stripe === undefined) {
      const error = this === undefined ? 'this' : 'this.stripe';
      SentryService.captureException(new Error(error));
      return null;
    }
    try {
      this.paymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: `${strips} Blood Glucose Test Strips / Month`,
          amount: Math.abs(total * 100),
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
        requestShipping: true,
        shippingOptions: SHIPPING_OPTIONS,
      });

      // we set this request via a call back early on. Should the vendor buttons show first and user clicks on it
      setPayRequest(this.paymentRequest);

      // Check the availability of the Payment Request API first for vendors e.g applepay
      const paymentChannel = await this.paymentRequest.canMakePayment();

      if (paymentChannel) {
        // set payment support to `true` if payment channels doesn't resolve to null
        // Set the stripe button payment request type (google pay OR apple pay)
        const paymentRequestSupported = true;
        const paymentRequestType = SubscribePaymentService.setPaymentRequestType(paymentChannel);
        vendorHandler(paymentRequestSupported, paymentRequestType);

        // ready payment listener for vendor
        this.paymentRequestListeners(payRequestHandler);

        // mount stripe default payment request button
        // this.stripe
        //   .elements()
        //   .create('paymentRequestButton', {
        //     paymentRequest: this.paymentRequest,
        //   })
        //   .mount('#payment-request-button');
      }
      // when we mount the default stripe button we use the paymentChannel to
      // override the useragent logic for showing the custom payment button type
      this.mountField(cardElement, eventHandler);
    } catch (error) {
      SentryService.captureException(error);
    }
  }

  /**
   * Mounts Stripe field
   * @param eventHandler
   */
  mountField(
    cardElement: HTMLDivElement,
    eventHandler?: (event: { elementType: 'card' }) => void
  ): void {
    if (this.cardElement) return;
    try {
      const elements = this.stripe.elements();
      // Mount the Stripe input card element
      // Stripe already mounted, skip
      if (cardElement.hasChildNodes()) {
        return;
      }

      // Else first mount
      this.card = elements.create('card', {
        hidePostalCode: true, // We're already collecting this elsewhere
        iconStyle: 'solid',
        style: STRIPE_INPUT_STYLING,
      });

      this.card.mount(cardElement);
      this.cardElement = cardElement;
      if (eventHandler) this.card.on('ready', eventHandler);
    } catch (error) {
      SentryService.captureException(error);
    }
  }

  static setPaymentRequestType(stripePaymentChannels: IPaymentRequestType): string {
    const { applePay, googlePay, microsoftPay } = stripePaymentChannels;

    if (applePay) {
      return 'applepay';
    }
    if (googlePay) {
      return 'googlepay';
    }
    if (microsoftPay) {
      return 'microsoftpay';
    }
    return 'genericPaymentRequest';
  }

  /**
   * Determines whether the payment
   */
  paymentRequestListeners(
    payRequestHandler: (event: PaymentRequestSourceEvent) => Promise<void>
  ): void {
    this.paymentRequest.on('shippingaddresschange', (event) => {
      if (event.shippingAddress.country !== 'US') {
        event.updateWith({ status: 'invalid_shipping_address' });
      } else {
        event.updateWith({
          status: 'success',
          shippingOptions: SHIPPING_OPTIONS,
        });
      }
    });

    // On payment source successfully added
    this.paymentRequest.on('source', payRequestHandler);
  }

  createSource(diffBillingAddress: boolean, checkoutData: ICheckoutData): Promise<SourceResult> {
    return SubscribeSourceService.createSource(
      diffBillingAddress,
      checkoutData,
      this.stripe,
      this.card
    );
  }
}

// We need to use it especially in OrderSummary.svelte, so better to export it from here.
// TODO: When we export it from here, it creates some circular dependency issues. We could revisit.
export type NewSourceResult = SourceResult;

export default SubscribePaymentService;
